// The doorway: the one way this site's calls reach the API, one area at a time (ai_app_backend decision 0015,
// "backend first"; docs/architecture.md, "Repointing an area to v2"). An area's calls live in their own module beside
// this one (accounts.ts), one function per call, and each goes out through `call` below. The doorway owns:
//
// - v1's address (NEXT_PUBLIC_PROMPTCHAN_CLOUD), so no call builds it itself;
// - the signed-in header: the person's Firebase ID token, raw in Authorization, with no "Bearer", as v1 reads it;
// - the answer: fetch's Response, untouched, because the screens branch on its status and read the body themselves.
//   v1 and v2 both put the words for the person in an error answer's `error` field, so a screen that shows
//   `error` reads either;
// - which API answers: a call may bring a v2 side, the same call on v2 answered in the shape its screen reads, and
//   the area's switch (switch.ts) decides per visitor whether it is used. Every area is `off` until Andy says
//   otherwise, and a call with no v2 side stays on v1 whatever its area's switch says;
// - what v2 can't yet do as v1 does (`V2_LACKS`): a call that needs it brings no v2 side till then, and stays on v1.
//
// An area's calls move onto the doorway with the area, never before: the other areas still build the address
// themselves until their own move. With its area off, a call on the doorway sends exactly the request its screen
// sent before (tests/accounts-api.test.mjs), so moving a call changes nothing a visitor or v1 can see.
//
// The switch loads only when it has something to decide (`await import`): a call with a v2 side, in a build with v2's
// address. Every other call, and every page's first load, carries none of it (CLAUDE.md, "First-load weight"). A
// switch that fails to load or to answer sends the call to v1.

/** v1's API (promptchan-backend), the address every call used before the doorway. */
export const V1_API = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD

/** v1's payments host (payment-backend, the same code as v1's API): where v1's checkouts and gem prices are asked. */
export const V1_PAYMENTS = process.env.NEXT_PUBLIC_PROMPTCHAN_PAYMENT

/**
 * Where a call goes on v1: a path on v1's API, `{ payments: path }` on v1's payments host, or a whole address (the
 * Create page's and chat's other hosts: v1's free pool and video machines), as given.
 */
export type V1Path = string | { payments: string }

const v1Url = (path: V1Path) =>
  typeof path !== 'string'
    ? `${V1_PAYMENTS}${path.payments}`
    : /^https?:\/\//.test(path)
      ? path
      : `${V1_API}${path}`

/** v2's API (ai_app_backend), when this build may call it: unset, every call goes to v1. Not a secret. */
export const V2_API = process.env.NEXT_PUBLIC_AI_APP_API

/** v1's second service (NEXT_PUBLIC_PROMPTCHAN_FREE), which answers chat's sends: a call there names its whole address. */
export const V1_FREE = process.env.NEXT_PUBLIC_PROMPTCHAN_FREE

/**
 * The areas of the site that move to v2 one at a time, each with its own switch. Room 1's is `accounts`; room 2's are
 * `billing` (the price lists and card checkouts) and `crypto` (crypto checkouts), as card payments go first and crypto
 * follows behind its own flag (ai_app_backend plan.pm, r2-rollout and r2-rollout-crypto). Room 3's Create page is
 * `create` (its pictures, edits, keeping and deleting) and `create_video` (its video panel): one area with two flags
 * in room 3's spec (ai_app_backend docs/specs/R3-makers-and-safety.md §10), here one switch each. Room 5's is `social`,
 * and `picture_signed_out` for the picture page's read without a sign-in, placed by the picture's id (R5-16).
 * Room 4's one-to-one chat is `chat` and its groups `chat_groups` (ai_app_backend docs/specs/R4-chat.md §10).
 * Search by words, the search page's and the picture page's related row, is `search` (its own flag, decision 0050 §5).
 * Sign-up is room 1's `signup`, its own area, placed by visitor id (R1-31): a person signing up has no account yet.
 * Room 4's Stories (the date game) is `stories` (ai_app_backend docs/specs/R4-S-stories.md §10). Room 6's growth
 * tracking is `growth` (visits, a browser's link, affiliate clicks, checkout-offer events: growth.js), placed by visitor
 * id as sign-up is.
 * The site's notice, which every page reads, signed in or not, is `site_notice` (room 6's; ai_app_backend plan.pm,
 * site-notice): placed by the uid when signed in, else by the visitor id, as no account is needed to read it.
 * Room 6's moderation is `moderation` (ai_app_backend plan.pm, r6-moderation): the report button, and staff's report
 * feed, approvals and removals (moderation.ts).
 */
export type Area =
  | 'accounts'
  | 'billing'
  | 'crypto'
  | 'create'
  | 'create_video'
  | 'chat'
  | 'chat_groups'
  | 'social'
  | 'search'
  | 'picture_signed_out'
  | 'signup'
  | 'stories'
  | 'growth'
  | 'site_notice'
  | 'moderation'

/** What v2 can't yet do as v1 does (`V2_LACKS`). */
export type V2Lack = 'country' | 'ageCheck' | 'gemClaims' | 'voiceCallAudio' | 'discoverCard'

/**
 * What v2 can't yet do as v1 does today. While one is `true`, the calls that need it keep to v1 for everyone, staff
 * and the flag's customers included, whatever their area's switch says, so a person on v2 gets v1's behaviour for
 * them unchanged. Their v2 sides stay written: each goes back to v2 when a pull request sets its line here to `false`,
 * once v2's own change is live in production (the only place to change).
 */
export const V2_LACKS: Record<V2Lack, boolean> = {
  // The visitor's country: v2 trusts one only behind Cloudflare (ai_app_backend's R1-20 part 4, its setting
  // AI_APP_BEHIND_CLOUDFLARE, off till then). Without it v2 asks nobody for a proof of age and offers the daily claim
  // to nobody. Keeps to v1: the age gate (the account document's `ageGate` and the safe mode it forces, and the
  // banner's Verify Age), and the earn-gems screen, whose daily claim pays by country.
  // False since production's API trusts Cloudflare's country (ai_app_backend #944, 23 October 2026).
  country: false,
  // Starting a proof of age needs production's API to hold Didit's key: false since it does (ai_app_backend #946, 23
  // October 2026). While true, the banner's Verify Age keeps to v1. Didit's notice reaches v1 either way, and v1 and v2
  // name the person the same way (`vendor_data`), so a check started on either counts on both.
  ageCheck: false,
  // Moving free gems: v2 answers 503 to claims and staff top-ups until v1's side door into the ledger closes (R1-06,
  // Andy's database step), and offers the X and Trustpilot tasks only once their links are set on its database. While
  // true, the earn-gems screen, welcome back's claim and the social team's top-up keep to v1. v1 pays them through the
  // same ledger (since R1-04), so the balance any screen shows after is the same either way. False since the side door
  // closed (20 October 2026) and both links are set on production's database (Andy, 23 October 2026).
  gemClaims: false,
  // A voice call's spoken reply: v2's `audio_url` is a key in its storage, not an address a browser can play, until
  // its signed links come (ai_app_backend's A86). Keeps to v1: the voice call's turn (chat.ts, a send with
  // `userVoice`). Every other chat call follows the chat area's switch.
  voiceCallAudio: true,
  // What Discover's card shows of a published character beyond v2's first answer: its occupation and where it lives,
  // and its animation, which every house character plays on the card (ai_app_backend's migration 5550, plan task
  // r4-discover). While true, Discover's page of characters and copying one (chat.ts, `readChatFeed` and
  // `matchProfile`) keep to v1. False since 5550 went live in production (ai_app_backend #896, 23 October 2026).
  discoverCard: false,
}

/** `v2`, the call's v2 side, unless v2 still lacks one of `needs` (`V2_LACKS`): then none, and the call keeps to v1. */
export const unlessV2Lacks = (
  needs: readonly V2Lack[],
  v2: { token: string; side: V2Side },
) => (needs.some((need) => V2_LACKS[need]) ? undefined : v2)

/** Headers for a signed-in call: `headers`, then the person's Firebase ID token as v1 reads it. */
export const signedIn = (
  token: string,
  headers: Record<string, string> = {},
): Record<string, string> => ({ ...headers, Authorization: token })

/** Signed-in JSON headers, the pair most of v1's calls send (with or without a body). */
export const signedInJson = (token: string) =>
  signedIn(token, { 'Content-Type': 'application/json' })

/**
 * The same call on v2, given the person's token and the call's own `init` (for its `signal`, the caller's time
 * limit): v2's answer in the shape the screen reads from v1's.
 */
export type V2Side = (token: string, init: RequestInit) => Promise<Response>

/** The header every answer a v2 side makes carries (v2.ts, `answer`), so a screen can tell which API answered. */
export const ANSWERED_BY = 'X-Answered-By'

/**
 * Whether v2 answered: for the few screens that keep v1's own behaviour on v1 and fix it on v2 only (the picture page's
 * "profile picture set", which v1's path shows whatever the answer).
 */
export const answeredByV2 = (response: Response) =>
  response.headers.get(ANSWERED_BY) === 'v2'

/**
 * v2's words for a refusal the screen shows as given: `error` of an answer with `status` and v2's `code` (v2 answers
 * an error as `{ error, code, retryable }`); null for any other answer, so the screen keeps its own text. v1 never
 * answers a `code`, so a v1 answer is always null. Reads a copy, so the screen may still read the body.
 */
export async function refusalWords(
  response: Response,
  status: number,
  code: string,
): Promise<string | null> {
  if (response.status !== status) return null
  try {
    const body = await response.clone().json()
    return body?.code === code && typeof body.error === 'string' && body.error
      ? body.error
      : null
  } catch {
    return null
  }
}

/**
 * A call's v2 side taken from a module loaded only when the side is used (`await import`, so it never adds to a page's
 * first load). A module that fails to load sends the call to v1 at `path` with its own `init`, as a switch that fails
 * does: an open tab after a deploy can fail to fetch the old chunk, and the call must still be answered.
 */
export const lazySide =
  <M>(
    load: () => Promise<M>,
    pick: (module: M) => V2Side,
    path: V1Path,
  ): V2Side =>
  async (token, init) => {
    let side: V2Side
    try {
      side = pick(await load())
    } catch {
      return fetch(v1Url(path), init)
    }
    return side(token, init)
  }

/**
 * One call through the doorway: `path` on v1's API (or, a whole address, on another of v1's hosts) with `init` as
 * given, or, when the call brings a v2 side and its area's switch puts this visitor on v2, that side. `token` is the
 * person's Firebase ID token.
 */
export async function call(
  area: Area,
  path: V1Path,
  init: RequestInit,
  v2?: {
    token: string
    side: V2Side
    /**
     * The call acts on something v2 made (one of its pictures, say), so v2 answers it whatever the switch says, as
     * long as this build has v2's address: after a switch back, v1's routes never act on v2's rows.
     */
    ownedByV2?: boolean
  },
  loadSwitch: () => Promise<Pick<typeof import('./switch.ts'), 'onV2'>> = () =>
    import('./switch.ts'),
): Promise<Response> {
  if (
    v2 &&
    V2_API &&
    (v2.ownedByV2 || (await toV2(area, v2.token, loadSwitch)))
  )
    return v2.side(v2.token, init)
  return fetch(v1Url(path), init)
}

/**
 * A read only v2 answers, which no v1 call has (the upgrade page's plans: v1's page keeps its own copy): v2's answer
 * for a visitor whose area's switch puts them on v2, and null for everyone else, a signed-out visitor included, so
 * the screen keeps what it showed before. A switch that fails, or a side that fails to load, also answers null.
 */
export async function onlyOnV2(
  area: Area,
  token: string | null,
  side: () => Promise<V2Side>,
  init: RequestInit = {},
  loadSwitch: () => Promise<Pick<typeof import('./switch.ts'), 'onV2'>> = () =>
    import('./switch.ts'),
): Promise<Response | null> {
  if (!token || !V2_API || !(await toV2(area, token, loadSwitch))) return null
  let read: V2Side
  try {
    read = await side()
  } catch {
    return null
  }
  return read(token, init)
}

/**
 * Whether the switch puts this call on v2. Any failure loading or running the switch answers false, so the call goes
 * to v1: an open tab after a deploy can fail to fetch the switch's old chunk, and a call with a v2 side must still be
 * answered (review C, 1). `loadSwitch` is replaced only by tests.
 */
async function toV2(
  area: Area,
  token: string,
  loadSwitch: () => Promise<Pick<typeof import('./switch.ts'), 'onV2'>>,
): Promise<boolean> {
  try {
    const { onV2 } = await loadSwitch()
    return await onV2(area, token)
  } catch {
    return false
  }
}
